import { isPlainObject } from 'lodash'; // FIXME: no idea why eslint is flagging this

/**
 * Pluralize a word.
 *
 * @example
 * pluralize( 1, 'apple' ); // '1 apple'
 * pluralize( 2, 'apple' ); // '2 apples'
 * pluralize( 0, 'apple' ); // '0 apples'
 * pluralize( 1, 'octopus', 'octopi' ); // '1 octopus'
 * pluralize( 2, 'octopus', 'octopi' ); // '2 octopi'
 * pluralize( 0, 'octopus', 'octopi' ); // '0 octopi'
 */
export function pluralize( count: number, single: string, plural: string = `${single}s` ): string {
  return `${count} ${count === 1 ? single : plural}`;
}

/**
 * Capitalize the first letter of a string.
 *
 * @example
 * capitalize( 'apple' ); // 'Apple'
 * capitalize( 'octopus' ); // 'Octopus'
 */
export function capitalize( string: string ): string {
  return string!.charAt( 0 ).toUpperCase() + string!.slice( 1 );
}

/**
 * @deprecated This is only used in one place, and is largely buggy. Invoking with scalars will
 * throw an error. Invoking with objects containing toString() will return the result of toString
 * *sometimes*, but not when the object is a "plain old javascript object".
 */
export function stringify( object: any, spaces = 2 ): string {
  if ( isPlainObject( object ) ) {
    return JSON.stringify( object, null, spaces );
  }

  const objectType = typeof object;

  if ( objectType === 'string' ) {
    return object;
  }

  if ( 'toString' in object ) {
    return object.toString();
  }

  if ( object === null ) {
    return 'null';
  }

  if ( objectType === 'undefined' ) {
    return 'undefined';
  }

  throw new TypeError( `stringify#object could not be converted to a String.` );
}

/**
 * Breaks apart compound characters into their constituent glyphs and strips diacritics. This helps
 * us treat e.g. “Pokémon” and “Pokemon” as equivalent. You can further convert to lowercase for
 * case-insensitive comparisons.
 *
 * @see {@link https://stackoverflow.com/a/37511463/214325}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize}
 */
export function unicodeNormalize( string: string ) {
  return string.normalize( 'NFD' ).replace( /\p{Diacritic}/gu, '' );
}

/**
 * Masks the middle of a credential-like string, preserving the start and end.
 *
 * @example
 * maskCredential( '1234567890' ); // '123...890'
 * maskCredential( '1234567890', 3, 3 ); // '123...890'
 * maskCredential( '1234567890', 3, 0 ); // '123...'
 * maskCredential( '1234567890', 3, 3, '****' ); // '123****890'
 */
export function maskCredential(
  value: string,
  visibleCharsStart: number = 3,
  visibleCharsEnd: number = visibleCharsStart,
  fill: string = '...',
): string {
  const safeStart = Math.max( 0, visibleCharsStart );
  const safeEnd = Math.max( 0, visibleCharsEnd );

  if ( safeStart === 0 && safeEnd === 0 ) {
    return fill;
  }

  const threshold = safeStart + safeEnd;

  if ( value.length <= threshold ) {
    return value;
  }

  const start = value.slice( 0, safeStart );
  const end = safeEnd > 0 ? value.slice( -safeEnd ) : '';

  return `${start}${fill}${end}`;
}

/* --- TODO: Move to numbers.ts --- */

export function generateRandomNumber( digits: number = 6 ) {
  const RANDOM_DIGITS_UPPER_LIMIT = 20.99999999999999;

  if ( digits >= RANDOM_DIGITS_UPPER_LIMIT ) {
    throw new RangeError(
      `generateRandomNumberString#digits is too high; got ${digits} and expected >= ${RANDOM_DIGITS_UPPER_LIMIT}.`,
    );
  }

  return Math.ceil( Math.random() * 10 ** digits );
}

export function generateRandomNumberString( digits: number = 6 ) {
  return generateRandomNumber( digits ).toString();
}

export function stripLeadingZeroes( digit: string ) {
  while ( digit[0] === '0' ) {
    digit = digit.slice( 1 );
  }

  return digit;
}

export function addLeadingZero( digit: string | number ) {
  let digitWithLeadingZero = String( digit );

  if ( typeof digit === 'string' ) {
    digit = Number( digit );
  }

  if ( digit < 10 ) {
    digitWithLeadingZero = `0${digit}`;
  }

  return digitWithLeadingZero;
}

/* --- /numbers.ts --- */

// https://dev.to/jorik/country-code-to-flag-emoji-a21
export function getFlagEmoji( countryCode: string ) {
  const codePoints = countryCode
    .toUpperCase()
    .split( '' )
    .map( ( char ) => 127397 + char.charCodeAt( 0 ) );

  return String.fromCodePoint( ...codePoints );
}

/**
 * Convert string representations of data types back into the real datatype. (Shallow)
 */
export function interpretValue( value: string ) {
  let interpretedValue: any = value;

  switch ( value ) {
    case 'true':
      interpretedValue = true;
      break;
    case 'false':
      interpretedValue = false;
      break;
    case 'null':
      interpretedValue = null;
      break;
    case 'undefined':
    case undefined:
      interpretedValue = undefined;
      break;
    default:
      if ( value[0] === '{' || value[0] === '[' ) {
        interpretedValue = JSON.parse( value );

        if ( Array.isArray( interpretedValue ) ) {
          interpretedValue = interpretedValue.map( ( item ) => interpretValue( item ) );
        }
      } else {
        const valueAsNumber = Number( value );

        if ( !Number.isNaN( valueAsNumber ) ) {
          interpretedValue = valueAsNumber;
        }
      }
  }

  return interpretedValue;
}
